import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axios";
import { ChevronLeft, ChevronRight } from "lucide-react";

const HeroSlider = () => {
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState("right");
  const [loading, setLoading] = useState(true);

  const fetchBanners = async () => {
    try {
      const resp = await axiosInstance.get("/banner");
      const data = resp?.data?.data || [];

      const sorted = data
        .filter((b) => b.isActive)
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

      setSlides(sorted);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  if (loading) {
    return (
      <div className="relative w-full overflow-hidden drop-shadow-xl">
        <div className="relative w-full lg:max-h-[79vh] aspect-[2160/833] bg-gray-300 animate-pulse rounded-lg">
          <div
            className="absolute inset-0 pointer-events-none z-10"
            style={{
              background: `
                    linear-gradient(
                    90deg,
                    rgba(0, 0, 0, 0.28) 0%,  
                    rgba(0, 0, 0, 0.17) 25%, 
                    rgba(0, 0, 0, 0.05) 50%, 
                    rgba(0, 0, 0, 0) 100%    
                )
                `,
            }}
          />
          {/* mimic title block */}
          <div className="absolute left-6 top-1/2 -translate-y-1/2 w-1/3 h-10 bg-gray-400/60 rounded" />
        </div>
      </div>
    );
  }

  if (!slides.length) return null;

  const next = () => {
    setDirection("right");
    setIndex((prev) => (prev + 1) % slides.length);
  };

  const prev = () => {
    setDirection("left");
    setIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative w-full overflow-hidden drop-shadow-xl">
      {/* NAVIGATION */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className=" items-center justify-center absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-50 p-1.5 sm:p-2 bg-black/40 rounded-full text-white hover:bg-black/60"
          >
            <ChevronLeft className="size-4 sm:size-5" />
          </button>

          <button
            onClick={next}
            className=" items-center justify-center absolute right-1 sm:right-2 md:right-4 top-1/2 -translate-y-1/2 z-50 p-1.5 sm:p-2 bg-black/40 rounded-full text-white hover:bg-black/60"
          >
            <ChevronRight className="size-4 sm:size-5" />
          </button>
        </>
      )}

      {/* SLIDE WRAPPER */}
      <div className="relative w-full lg:max-h-[79vh] aspect-[2160/833] bg-black overflow-hidden">
        {slides.map((slide, i) => {
          const isActive = i === index;

          return (
            <div key={i} className="absolute inset-0 w-full h-full">
              {/* Image */}
              <img
                src={slide.image}
                className={`
                  absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-[cubic-bezier(.4,0,.2,1)]
                  ${isActive ? "opacity-100 translate-x-0" : "opacity-0"}
                  ${
                    !isActive
                      ? direction === "right"
                        ? "translate-x-10"
                        : "-translate-x-10"
                      : ""
                  }
                `}
                alt={`banner-${i}`}
                style={{ pointerEvents: "none" }}
              />

              {/* Title */}
              {isActive && (
                <div
                  className={`
                    absolute inset-0 flex items-center pointer-events-none
                    transition-opacity duration-700
                    ${isActive ? "opacity-100" : "opacity-0"}
                  `}
                >
                  <div className="px-8 md:px-10 lg:px-16 xl:px-20 z-20 w-full pointer-events-none">
                    <h1
                      className={`
                        text-white font-semibold
                        leading-[1.1]
                        tracking-wide
                        max-w-[50%]
                        text-left
                        select-none
                        text-2xl 
                        sm:text-4xl
                        md:text-5xl
                        lg:text-6xl
                        xl:text-8xl
                        ${!slide.showGradient ? 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]' : 'drop-shadow-xl'}
                      `}
                      title={slide.title}
                    >
                      {slide.title}
                    </h1>
                  </div>
                </div>
              )}

              {slide.showGradient !== false && (
                <div
                  className={`
                    absolute inset-0 pointer-events-none z-10 transition-opacity duration-700
                    ${isActive ? "opacity-100" : "opacity-0"}
                  `}
                  style={{
                    background: `
                      linear-gradient(
                      90deg,
                      rgba(0, 0, 0, 0.45) 0%,  
                      rgba(0, 0, 0, 0.25) 25%, 
                      rgba(0, 0, 0, 0.1) 50%, 
                      rgba(0, 0, 0, 0) 100%    
                  )
                  `,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* DOTS */}
      {slides.length > 1 && (
        <div className="absolute bottom-2 lg:bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 lg:gap-2 z-20">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setDirection(i > index ? "right" : "left");
                setIndex(i);
              }}
              className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full transition ${
                i === index ? "bg-white" : "bg-white/40"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroSlider;

// import React, { useState, useEffect } from "react";
// import axiosInstance from "../../../utils/axios";
// import { ChevronLeft, ChevronRight } from "lucide-react";

// const HeroSlider = () => {
//   const [slides, setSlides] = useState([]);
//   const [index, setIndex] = useState(0);
//   const [direction, setDirection] = useState("right");
//   const [loading, setLoading] = useState(true);

//   const fetchBanners = async () => {
//     try {
//       const resp = await axiosInstance.get("/banner");
//       const data = resp?.data?.data || [];

//       const sorted = data
//         .filter((b) => b.isActive)
//         .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

//       setSlides(sorted);
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBanners();
//   }, []);

//   if (loading) {
//     return (
//       <div className="w-full aspect-[2160/833] bg-gray-200 animate-pulse rounded-lg" />
//     );
//   }

//   if (!slides.length) return null;

//   const next = () => {
//     setDirection("right");
//     setIndex((prev) => (prev + 1) % slides.length);
//   };

//   const prev = () => {
//     setDirection("left");
//     setIndex((prev) => (prev - 1 + slides.length) % slides.length);
//   };

//   return (
//     <div className="relative w-full overflow-hidden drop-shadow-xl">
//       {/* NAVIGATION */}
//       {slides.length > 1 && (
//         <>
//           <button
//             onClick={prev}
//             className=" items-center justify-center absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/40 rounded-full text-white hover:bg-black/60"
//           >
//             <ChevronLeft className="size-5" />
//           </button>

//           <button
//             onClick={next}
//             className=" items-center justify-center absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/40 rounded-full text-white hover:bg-black/60"
//           >
//             <ChevronRight className="size-5" />
//           </button>
//         </>
//       )}

//       {/* SLIDE WRAPPER */}
//       <div className="relative w-full lg:max-h-[79vh] aspect-[2160/833] bg-black overflow-hidden">
//         {slides.map((slide, i) => {
//           const isActive = i === index;

//           return (
//             <div key={i} className="absolute inset-0 w-full h-full">
//               {/* Image */}
//               <img
//                 src={slide.image}
//                 className={`
//                   absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-[cubic-bezier(.4,0,.2,1)]
//                   ${isActive ? "opacity-100 translate-x-0" : "opacity-0"}
//                   ${
//                     !isActive
//                       ? direction === "right"
//                         ? "translate-x-10"
//                         : "-translate-x-10"
//                       : ""
//                   }
//                 `}
//                 alt={`banner-${i}`}
//                 style={{ pointerEvents: "none" }}
//               />

//               {isActive && (
//                 <div
//                   className={`
//                     absolute inset-0 flex items-center
//                     transition-opacity duration-700
//                     ${isActive ? "opacity-100" : "opacity-0"}
//                   `}
//                 >
//                   <div className="px-6 md:px-10 lg:px-16 xl:px-20 z-20">
//                     <h1
//                       className="
//                         text-white font-semibold
//                         leading-[1.1]
//                         tracking-wide
//                         max-w-[70%]
//                         text-left
//                         drop-shadow-xl
//                         select-none
//                         pointer-events-none
//                         text-2xl
//                         sm:text-4xl
//                         md:text-5xl
//                         lg:text-6xl
//                         xl:text-7xl
//                       "
//                     >
//                       {slide.title}
//                     </h1>
//                   </div>
//                 </div>
//               )}
//               {/* black gradient overlay left->right */}
//               <div
//                 className="absolute inset-0 pointer-events-none z-1"
//                 style={{
//                   background:
//                     "linear-gradient(90deg, rgba(0,0,0,0.65) 10%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.0) 10%)",
//                 }}
//               />
//             </div>
//           );
//         })}
//       </div>

//       {/* DOTS */}
//       {slides.length > 1 && (
//         <div className="absolute bottom-2 lg:bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 lg:gap-2 z-20">
//           {slides.map((_, i) => (
//             <button
//               key={i}
//               onClick={() => {
//                 setDirection(i > index ? "right" : "left");
//                 setIndex(i);
//               }}
//               className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full transition ${
//                 i === index ? "bg-white" : "bg-white/40"
//               }`}
//             />
//           ))}
//         </div>
//       )}
//     </div>
//   );
// };

// export default HeroSlider;

// // import React, { useState } from "react";
// // import { ChevronLeft, ChevronRight } from "lucide-react";

// // const HeroSlider = ({ slides = [] }) => {
// //   const [index, setIndex] = useState(0);
// //   const [direction, setDirection] = useState("right");

// //   if (!slides.length) return null;

// //   const next = () => {
// //     setDirection("right");
// //     setIndex((prev) => (prev + 1) % slides.length);
// //   };

// //   const prev = () => {
// //     setDirection("left");
// //     setIndex((prev) => (prev - 1 + slides.length) % slides.length);
// //   };

// //   return (
// //     <div className="relative w-full overflow-hidden drop-shadow-xl">
// //       {/* NAVIGATION */}
// //       {slides.length > 1 && (
// //         <>
// //           <button
// //             onClick={prev}
// //             className=" items-center justify-center absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/40 rounded-full text-white hover:bg-black/60"
// //           >
// //             <ChevronLeft className="size-5" />
// //           </button>

// //           <button
// //             onClick={next}
// //             className=" items-center justify-center absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/40 rounded-full text-white hover:bg-black/60"
// //           >
// //             <ChevronRight className="size-5" />
// //           </button>
// //         </>
// //       )}

// //       {/* SLIDE WRAPPER */}
// //       <div className="relative w-full  lg:max-h-[79vh] aspect-[2160/833] bg-black overflow-hidden">
// //         {slides.map((src, i) => {
// //           const isActive = i === index;

// //           return (
// //             <img
// //               key={i}
// //               src={src}
// //               className={`
// //                 absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-[cubic-bezier(.4,0,.2,1)]
// //                 ${isActive ? "opacity-100 translate-x-0" : "opacity-0"}
// //                 ${
// //                   !isActive
// //                     ? direction === "right"
// //                       ? "translate-x-10"
// //                       : "-translate-x-10"
// //                     : ""
// //                 }
// //               `}
// //               style={{ pointerEvents: "none" }}
// //               alt="banner"
// //             />
// //           );
// //         })}
// //       </div>

// //       {/* DOTS */}
// //       {slides.length > 1 && (
// //         <div className="absolute bottom-2 lg:bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 lg:gap-2 z-20">
// //           {slides.map((_, i) => (
// //             <button
// //               key={i}
// //               onClick={() => {
// //                 setDirection(i > index ? "right" : "left");
// //                 setIndex(i);
// //               }}
// //               className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full transition ${
// //                 i === index ? "bg-white" : "bg-white/40"
// //               }`}
// //             />
// //           ))}
// //         </div>
// //       )}
// //     </div>
// //   );
// // };

// // export default HeroSlider;
